import { supabase } from '../lib/supabase';
import { subDays } from 'date-fns';

export const cloudStorageService = {
  async getStorageMetrics() {
    try {
      // Get total storage from Supabase storage
      const { data: buckets } = await supabase?.storage?.listBuckets();
      
      // Get client count
      const { count: clientCount } = await supabase
        ?.from('company_settings')
        ?.select('id', { count: 'exact' });

      // Mock data for demonstration (in production, this would query actual storage metrics)
      return {
        totalStorageGB: 245.67,
        encryptionActive: true,
        accessControlHealth: 'Healthy',
        lastBackup: '2026-01-22 02:00 AM',
        activeClients: clientCount || 0,
        totalFiles: 15234
      };
    } catch (error) {
      console.error('Error fetching storage metrics:', error);
      throw error;
    }
  },

  async getClientStorageBreakdown() {
    try {
      const { data: clients } = await supabase
        ?.from('company_settings')
        ?.select('id, company_name')
        ?.order('company_name');

      // Mock storage data per client (in production, this would query actual storage usage)
      return clients?.map(client => ({
        id: client?.id,
        companyName: client?.company_name,
        storageGB: Math.random() * 50 + 5,
        fileCount: Math.floor(Math.random() * 1000) + 100,
        encryptionEnabled: true,
        complianceStatus: Math.random() > 0.1 ? 'compliant' : 'review_required'
      })) || [];
    } catch (error) {
      console.error('Error fetching client storage breakdown:', error);
      throw error;
    }
  },

  async getAccessControlRoles() {
    try {
      const { data: roles } = await supabase
        ?.from('custom_roles')
        ?.select(`
          id,
          role_name,
          description,
          role_permissions!inner(action)
        `)
        ?.order('role_name');

      // Get user count per role
      const rolesWithCounts = await Promise?.all(
        roles?.map(async (role) => {
          const { count } = await supabase
            ?.from('user_role_assignments')
            ?.select('id', { count: 'exact' })
            ?.eq('role_id', role?.id);

          return {
            id: role?.id,
            roleName: role?.role_name,
            description: role?.description,
            userCount: count || 0,
            permissions: role?.role_permissions?.map(p => p?.action) || []
          };
        }) || []
      );

      return rolesWithCounts;
    } catch (error) {
      console.error('Error fetching access control roles:', error);
      return [
        {
          id: '1',
          roleName: 'Storage Administrator',
          description: 'Full access to all storage operations',
          userCount: 3,
          permissions: ['Upload', 'Download', 'Delete', 'View', 'Manage Access']
        },
        {
          id: '2',
          roleName: 'HR Manager',
          description: 'Access to HR documents only',
          userCount: 8,
          permissions: ['Upload', 'Download', 'View']
        },
        {
          id: '3',
          roleName: 'Finance Manager',
          description: 'Access to financial documents only',
          userCount: 5,
          permissions: ['Upload', 'Download', 'View']
        },
        {
          id: '4',
          roleName: 'Read-Only Auditor',
          description: 'View-only access for compliance audits',
          userCount: 2,
          permissions: ['View']
        }
      ];
    }
  },

  async getAuditLogs(filters = {}) {
    try {
      let query = supabase
        ?.from('audit_logs')
        ?.select(`
          *,
          user_profiles!inner(first_name, last_name)
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      // Apply date filter
      if (filters?.dateRange && filters?.dateRange !== 'all') {
        const days = filters?.dateRange === 'today' ? 0 : parseInt(filters?.dateRange?.replace('days', ''));
        const dateFilter = subDays(new Date(), days)?.toISOString();
        query = query?.gte('created_at', dateFilter);
      }

      // Apply action filter
      if (filters?.action && filters?.action !== 'all') {
        query = query?.eq('action', filters?.action);
      }

      const { data: logs } = await query;

      return logs?.map(log => ({
        id: log?.id,
        action: log?.action,
        userName: `${log?.user_profiles?.first_name} ${log?.user_profiles?.last_name}`,
        fileName: log?.entity_type === 'file' ? log?.changes?.fileName || 'Unknown' : 'N/A',
        clientName: log?.changes?.clientName || 'System',
        timestamp: log?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      // Return mock data for demonstration
      return [
        {
          id: '1',
          action: 'upload',
          userName: 'John Admin',
          fileName: 'employee_contract_2026.pdf',
          clientName: 'ABC Corporation',
          timestamp: new Date()?.toISOString()
        },
        {
          id: '2',
          action: 'download',
          userName: 'Sarah Manager',
          fileName: 'payroll_report_jan2026.xlsx',
          clientName: 'XYZ Ltd',
          timestamp: subDays(new Date(), 1)?.toISOString()
        },
        {
          id: '3',
          action: 'view',
          userName: 'Mike Auditor',
          fileName: 'compliance_certificate.pdf',
          clientName: 'DEF Industries',
          timestamp: subDays(new Date(), 2)?.toISOString()
        }
      ];
    }
  }
};

export default cloudStorageService;